import { toSignificant, toPercentage } from '@simplywallst/formatting';
import DiscountRate from './DiscountRate';
import IntrinisicValueSummary from './IntrinsicValueSummary';
import DataTable from '@components/DataTable';
import { SWS_ANALYSIS_MODEL } from '@/constants';

interface Props {
  uniqueSymbol: string;
  reportingCurrencyISO: string;
  reportingCurrencySymbol: string;
  primaryTradingCurrecyISO: string;
  primaryTradingCurrencySymbol: string;
  tradingItemCurrencyISO: string;
  tradingItemCurrencySymbol: string;
  primaryListingExchangeSymbol: string;
  primaryListingTickerSymbol: string;
  lastEarningsUpdate: number;
  analystCount: number;
  analystFcfCountAverage: number;
  companyShortName: string;
  exchangeSymbol: string;
  tickerSymbol: string;
  exchangeCountryISO: string;
  listingPrice: number;
  industrySecondaryName: string;

  relativeListingSharesOutstanding: number;

  model: string;
  unleveredBeta: number;
  de: number;
  taxRate: number;
  equityPremium: number;
  riskFreeRate: number;
  leveredBetaActual: number;
  leveredBeta: number;
  costOfEquity: number;
  adrPerShare: number;

  pv5y: number;
  terminalValue: number;
  pvtv: number;
  npv: number;
  npvPerShare: number;
  npvPerShareReportedCurrency?: number;
  intrinsicDiscount: number;

  REIT?: {
    NAV: number;
    NAVCount: number;
  };

  dividendDiscount?: {
    DPS: number;
    payout: number;
    ROE: number;
    expectedGrowth: number;
    DDMGrowth: number;
    DDMSource: string;
  };

  twoStagefcf?: {
    affoEstimates?: number;
    growthCAGR5y: number;
    scale: string;
    scaleNumeric: number;
    sharesOutstanding: number;
    fcfsource?: string;
    fcfData: { date: number; value: number }[];
    fcfCount: { date: number; value: number }[];
    fcfLtm: number;
    firstStage: {
      year: number;
      data: number;
      source: string;
      discounted: number;
    }[];
  };

  excessReturns?: {
    stableBookValue: number;
    returnOnEquityAvg: number;
    stableEPS: number;
    stableEPSSource: string;
    stableBookValueSource: string;
    bookValue: number;
    equityCost: number;
    excessReturn: number;
  };
}

/** Valuation Models */
const TWO_STAGE_FCF_TO_EQUITY = '2 Stage Free Cash Flow to Equity';
const TWO_STAGE_FCF_TO_EQUITY_ADJUSTED_OPS_FUND =
  '2 Stage Free Cash Flow to Equity using Adjusted Funds From Operations';
const TWO_STAGE_FCF_TO_EQUITY_OPS_FUND =
  '2 Stage Free Cash Flow to Equity using Funds From Operations';
const DIVIDEND_DISCOUNT_MODEL = 'Dividend Discount Model';
const EXCESS_RETURNS_MODEL = 'Excess Returns Model';

const Content = ({
  uniqueSymbol,
  lastEarningsUpdate,
  reportingCurrencyISO,
  reportingCurrencySymbol,
  primaryTradingCurrecyISO,
  primaryTradingCurrencySymbol,
  tradingItemCurrencyISO,
  tradingItemCurrencySymbol,
  analystCount,
  analystFcfCountAverage,
  companyShortName,
  exchangeSymbol,
  tickerSymbol,
  exchangeCountryISO,
  listingPrice,
  industrySecondaryName,
  relativeListingSharesOutstanding,
  model,
  unleveredBeta,
  de,
  taxRate,
  equityPremium,
  riskFreeRate,
  leveredBetaActual,
  leveredBeta,
  costOfEquity,
  adrPerShare,
  pv5y,
  terminalValue,
  pvtv,
  npv,
  npvPerShare,
  npvPerShareReportedCurrency,
  intrinsicDiscount,
  REIT,
  dividendDiscount,
  twoStagefcf,
  excessReturns,
  primaryListingExchangeSymbol,
  primaryListingTickerSymbol,
}: Props) => (
  <div>
    <p>
      {`Below are the data sources, inputs and calculation used to determine the intrinsic value for ${companyShortName}.`}
    </p>
    <DataTable light>
      <caption>{`${uniqueSymbol} Discounted Cash Flow Data Sources`}</caption>
      <thead>
        <tr>
          <th>Data Point</th>
          <th>Source</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Valuation Model</td>
          <td />
          <td>{model}</td>
        </tr>
        {model === TWO_STAGE_FCF_TO_EQUITY && (
          <tr>
            <td>Levered Free Cash Flow</td>
            <td>
              {analystFcfCountAverage > 0
                ? `Up to ${toSignificant(
                    analystFcfCountAverage,
                    0
                  )} Analyst Estimates on Average (S&P Global)`
                : 'Extrapolated from most recent financials.'}
            </td>
            <td>See below</td>
          </tr>
        )}
        {model === TWO_STAGE_FCF_TO_EQUITY_ADJUSTED_OPS_FUND && (
          <tr>
            <td>Levered Adjusted Funds From Operations</td>
            <td>
              {`Up to ${toSignificant(
                analystCount,
                0
              )} Analyst Estimates on Average (S&P Global)`}
            </td>
            <td>See below</td>
          </tr>
        )}
        {model === TWO_STAGE_FCF_TO_EQUITY_OPS_FUND && (
          <tr>
            <td>Levered Funds From Operations</td>
            <td>
              {`Up to ${toSignificant(
                analystCount,
                0
              )} Analyst Estimates on Average (S&P Global)`}
            </td>
            <td>See below</td>
          </tr>
        )}
        {model === DIVIDEND_DISCOUNT_MODEL && dividendDiscount && (
          <tr>
            <td>Dividend Per Share</td>
            <td>
              {`Expected Dividends (${new Date(
                lastEarningsUpdate
              ).toLocaleDateString()}) in ${reportingCurrencyISO}`}
            </td>
            <td>{`${reportingCurrencySymbol} ${dividendDiscount.DPS}`}</td>
          </tr>
        )}
        {model === DIVIDEND_DISCOUNT_MODEL && dividendDiscount && (
          <tr>
            <td>Payout Ratio</td>
            <td>
              {`Company Filings (${new Date(
                lastEarningsUpdate
              ).toLocaleDateString()})`}
            </td>
            <td>{toPercentage(dividendDiscount.payout as number, 1)}</td>
          </tr>
        )}
        {model === EXCESS_RETURNS_MODEL && excessReturns && (
          <tr>
            <td>Stable EPS</td>
            <td>
              <span>{excessReturns.stableEPSSource}</span>
              <br />
              {excessReturns.returnOnEquityAvg &&
                excessReturns.returnOnEquityAvg > 0 && (
                  <>
                    <span>= Stable Book Value * Return on Equity</span>
                    <br />
                    <span>
                      {`= ${reportingCurrencySymbol}${toSignificant(
                        excessReturns.stableBookValue,
                        2
                      )} *${toPercentage(excessReturns.returnOnEquityAvg, 1)} `}
                    </span>
                  </>
                )}
            </td>
            <td>
              {`${reportingCurrencySymbol} ${toSignificant(
                excessReturns.stableEPS,
                2
              )}`}
            </td>
          </tr>
        )}
        {model === EXCESS_RETURNS_MODEL &&
          excessReturns &&
          excessReturns.returnOnEquityAvg &&
          excessReturns.returnOnEquityAvg > 0 && (
            <tr>
              <td>Book Value of Equity per Share</td>
              <td>{excessReturns.stableBookValueSource}</td>
              <td>
                {`${reportingCurrencySymbol} ${toSignificant(
                  excessReturns.stableBookValue,
                  2
                )}`}
              </td>
            </tr>
          )}
        <tr>
          <td>Discount Rate (Cost of Equity)</td>
          <td>See below</td>
          <td>{toPercentage(costOfEquity, 1)}</td>
        </tr>
        <tr>
          <td>Perpetual Growth Rate</td>
          {model === DIVIDEND_DISCOUNT_MODEL &&
          dividendDiscount &&
          dividendDiscount.DDMSource &&
          dividendDiscount.DDMGrowth ? (
            <>
              <td>{dividendDiscount.DDMSource}</td>
              <td>{toPercentage(dividendDiscount.DDMGrowth, 1)}</td>
            </>
          ) : (
            <>
              <td>{`5-Year Average of ${exchangeCountryISO} Long-Term Govt Bond Rate`}</td>
              <td>{toPercentage(riskFreeRate, 1)}</td>
            </>
          )}
        </tr>
      </tbody>
    </DataTable>
    <p>
      An important part of a discounted cash flow is the discount rate, below we
      explain how it has been calculated.
    </p>
    <DiscountRate
      uniqueSymbol={uniqueSymbol}
      exchangeCountryISO={exchangeCountryISO}
      industrySecondaryName={industrySecondaryName}
      unleveredBeta={unleveredBeta}
      de={de}
      taxRate={taxRate}
      equityPremium={equityPremium}
      riskFreeRate={riskFreeRate}
      leveredBetaActual={leveredBetaActual}
      leveredBeta={leveredBeta}
      costOfEquity={costOfEquity}
      twoStagefcf={twoStagefcf}
    />
    <p> </p>
    <IntrinisicValueSummary
      uniqueSymbol={uniqueSymbol}
      reportingCurrencyISO={reportingCurrencyISO}
      reportingCurrencySymbol={reportingCurrencySymbol}
      primaryTradingCurrecyISO={primaryTradingCurrecyISO}
      primaryTradingCurrencySymbol={primaryTradingCurrencySymbol}
      tradingItemCurrencyISO={tradingItemCurrencyISO}
      tradingItemCurrencySymbol={tradingItemCurrencySymbol}
      companyShortName={companyShortName}
      exchangeSymbol={exchangeSymbol}
      tickerSymbol={tickerSymbol}
      listingPrice={listingPrice}
      relativeListingSharesOutstanding={relativeListingSharesOutstanding}
      model={model}
      riskFreeRate={riskFreeRate}
      costOfEquity={costOfEquity}
      adrPerShare={adrPerShare}
      pv5y={pv5y}
      terminalValue={terminalValue}
      pvtv={pvtv}
      npv={npv}
      npvPerShare={npvPerShare}
      npvPerShareReportedCurrency={npvPerShareReportedCurrency}
      intrinsicDiscount={intrinsicDiscount}
      REIT={REIT}
      dividendDiscount={dividendDiscount}
      twoStagefcf={twoStagefcf}
      excessReturns={excessReturns}
      primaryListingExchangeSymbol={primaryListingExchangeSymbol}
      primaryListingTickerSymbol={primaryListingTickerSymbol}
    />
    <p>
      <span>{`Learn more about our DCF calculations in Simply Wall St’s `}</span>
      <a
        href={`${SWS_ANALYSIS_MODEL}/MODEL.markdown#value`}
        data-unique-symbol={uniqueSymbol}
        data-about-section="valuation_dcf"
        data-link-name="dcf"
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        analysis model.
      </a>
    </p>
  </div>
);

export default Content;
